import { posDb, deviceId, getCachedTenantCurrency, type DraftBill, type DraftBillItem, type DraftBillDiscount, type DraftPendingDiscountApproval } from '@/db/pos-database';
import { getActiveRegisterSessionId } from '@/stores/use-register-store';
import { encryptForStorage, decryptFromStorage, decryptManyFromStorage } from '@/db/encrypted-access';
import { enqueue } from '@/services/sync-queue.service';
import { gstWaived, roundBillTotal } from '@/lib/gst';
import { cashRoundingRuleFor } from '@/lib/cash-rounding';
import { getTaxRegime, type BillPaymentMethod, type CurrencyCode, type TaxRegime } from '@bbpos/types';
import type { BillDiscount, CartLineItem } from '@/stores/use-cart-store';
import type { BillWithItems } from '@/services/billing.service';

// ─── Types ──────────────────────────────────────────────────────────────────

interface OfflineBillPayload {
  client_id: string;
  location_id: string | null;
  items: Array<{
    product_id: string;
    name: string;
    sku: string;
    unit: string;
    quantity: number;
    unit_price: number;
    unit_cost: number;
    gst_rate: number;
    hsn_code?: string;
    discount_type?: 'PERCENTAGE' | 'FLAT';
    discount_value?: number;
  }>;
  customer_id: string | null;
  bill_number: string;
  payment_method: string;
  amount_paid: number;
  // The cash round-off the till applied (signed minor units), sent like the
  // online checkout's `rounding_adjustment`. Without it a rounded-down cash
  // total is below the grand_total the server rebuilds and the sync conflicts.
  rounding_adjustment?: number;
  // T12: the register session the till was on; the server stamps the bill with it.
  register_session_id?: string;
  // Bill-level (F8 / voucher) discount, in the server's ApplyDiscountDto units:
  // basis points for PERCENTAGE (1000=10%), paise for FLAT. The server re-applies
  // this at sync so the rebuilt grand_total matches the discounted amount_paid
  // the customer paid (#274). Absent when no bill-level discount was applied.
  bill_discount?: {
    type: 'PERCENTAGE' | 'FLAT';
    value: number;
    reason: string;
  };
  subtotal: number;
  discount_total: number;
  cgst_total: number;
  sgst_total: number;
  igst_total: number;
  grand_total: number;
  /** Legacy: the server never reads it (gst-parked 040). */
  is_inter_state: boolean;
  /**
   * gst-parked D7: the cashier's IGST switch. Absent when nobody switched —
   * the server then stamps the store's own split.
   */
  supply_override?: 'INTRA' | 'INTER';
  payments?: Array<{ payment_method: string; amount: number }>;
  created_at: string;
  /** T19: slip / approval / UPI reference for a card/UPI payment taken offline. */
  payment_reference?: string;
}

/**
 * T19: the totals the cashier saw on the cart (bill discount and cash round-off
 * included), printed on the offline receipt. Minor units of the tenant currency.
 */
export interface OfflineReceiptTotals {
  subtotal: number;
  discountTotal: number;
  taxTotal: number;
  /** Before the cash round-off. */
  grandTotal: number;
  roundingAdjustment: number;
}

export interface OfflineBillResult {
  clientId: string;
  /** The temporary number printed on the receipt (LOC-YYYYMMDD-OFLnnnn). */
  billNumber: string;
  /** The bill as the receipt modal and the thermal printer read it. */
  receiptBill: BillWithItems;
}

// ─── Local sequence counter ─────────────────────────────────────────────────

const LOCAL_SEQ_KEY = 'bbpos_offline_bill_seq';

function getNextLocalSequence(): number {
  const current = parseInt(localStorage.getItem(LOCAL_SEQ_KEY) ?? '0', 10);
  const next = current + 1;
  localStorage.setItem(LOCAL_SEQ_KEY, String(next));
  return next;
}

function generateOfflineBillNumber(locationCode: string): string {
  const now = new Date();
  const dateStr =
    String(now.getFullYear()) +
    String(now.getMonth() + 1).padStart(2, '0') +
    String(now.getDate()).padStart(2, '0');
  const seq = getNextLocalSequence();
  return `${locationCode}-${dateStr}-OFL${String(seq).padStart(4, '0')}`;
}

// ─── Regime-aware per-line tax rounding ─────────────────────────────────────

/**
 * Offline mirror of the server's regime-aware per-line tax rounding
 * (`services/api/src/billing/tax-presentation.ts` `roundLineTax`, task 250).
 * Kept formula-identical so an offline-computed bill rounds exactly the way
 * the server will when it re-derives the bill at sync:
 *
 *  - `GST_INDIA` — `Math.floor` per line (the `CLAUDE.md` GST rule). For INR
 *    this is byte-identical to the legacy inline `Math.floor(... / 100)`, so
 *    INR offline totals are unchanged.
 *  - GCC VAT / `NO_VAT` — round-half-up to one whole currency minor unit
 *    (1/100 for 2-decimal currencies, 1/1000 for the 3-decimal OMR/BHD/KWD) —
 *    `rawTax` is already in that unit, so it is rounded to a whole number.
 *
 * `rawTax` is the unrounded per-line tax in the tenant's currency minor units
 * (`taxable * rate / 100`, where the `/ 100` is rate-percentage math, not a
 * minor-unit divisor).
 */
function roundLineTax(rawTax: number, regime: TaxRegime, _currency: CurrencyCode): number {
  if (regime === 'GST_INDIA') {
    return Math.floor(rawTax);
  }
  // `rawTax` is already in minor units, so half-up to a whole number is
  // half-up to the currency's precision. A round trip through major units
  // turns an exact x.5 (101.5 fils) into 101.4999… and rounds it down.
  return Math.floor(rawTax + 0.5);
}

// ─── Compute tax totals client-side ─────────────────────────────────────────

interface ComputedLine {
  lineSubtotal: number;
  lineDiscount: number;
  lineTax: number;
}

function computeItemTotals(
  items: CartLineItem[],
  isInterState: boolean,
  currency: CurrencyCode,
  locationId: string | null,
): {
  subtotal: number;
  discountTotal: number;
  cgstTotal: number;
  sgstTotal: number;
  igstTotal: number;
  grandTotal: number;
  /** T19: per-line figures, in `items` order, for the offline receipt. */
  lines: ComputedLine[];
} {
  // Tax regime is derived from the tenant's immutable currency (task 230).
  const regime = getTaxRegime(currency);

  let subtotal = 0;
  let discountTotal = 0;
  let cgstTotal = 0;
  let sgstTotal = 0;
  let igstTotal = 0;
  const lines: ComputedLine[] = [];

  for (const item of items) {
    const lineSubtotal = Math.round(item.unitPrice * item.quantity);
    subtotal += lineSubtotal;

    let discountAmount = 0;
    if (item.discountType === 'PERCENTAGE') {
      // `/ 100` here is percentage math (discountValue is a percent), NOT a
      // currency minor-unit divisor — correct for every regime/currency.
      discountAmount = Math.floor((lineSubtotal * item.discountValue) / 100);
    } else if (item.discountType === 'FLAT') {
      discountAmount = Math.min(item.discountValue, lineSubtotal);
    }
    discountTotal += discountAmount;

    const taxable = lineSubtotal - discountAmount;
    // `* gstRate / 100` is rate-percentage math; the regime decides how the
    // resulting fractional minor-unit tax collapses to a whole minor unit.
    const rawLineTax = (taxable * item.gstRate) / 100;

    let lineTax: number;
    if (regime === 'GST_INDIA' && gstWaived()) {
      // Not GST registered (gst-parked 090, D5): no GST on the offline bill.
      lineTax = 0;
    } else if (regime === 'GST_INDIA') {
      // India GST: CGST/SGST split intra-state, IGST inter-state. Floor the
      // full-rate tax once per line — matching the server (createBill /
      // applyDiscount) — then split for display. Flooring the half-rate
      // twice (the prior code) dropped 1 paise per line on odd line taxes,
      // making the offline-bill grand_total 1 paise below the server's and
      // breaking the completeBill paid-vs-total check (issue #94).
      lineTax = roundLineTax(rawLineTax, regime, currency);
      if (isInterState) {
        igstTotal += lineTax;
      } else {
        // The server's split: SGST = floor(tax / 2), CGST the odd paisa (gst-parked 080).
        const sgst = Math.floor(lineTax / 2);
        sgstTotal += sgst;
        cgstTotal += lineTax - sgst;
      }
    } else {
      // GCC: a single VAT line (no CGST/SGST/IGST split — plan §7, task 240),
      // round-half-up to the currency minor unit. The combined VAT is carried
      // in the single tax bucket; the server re-derives the VAT line at sync.
      lineTax = roundLineTax(rawLineTax, regime, currency);
      igstTotal += lineTax;
    }
    lines.push({ lineSubtotal, lineDiscount: discountAmount, lineTax });
  }

  const taxTotal = cgstTotal + sgstTotal + igstTotal;
  const rawTotal = subtotal - discountTotal + taxTotal;
  // Cash rounding by the till location's rule (ops-simulation T07) — the same
  // helper as the online cart and the API, read from the cached /auth/me
  // profile so an offline till rounds exactly as it would online.
  const grandTotal = roundBillTotal(rawTotal, cashRoundingRuleFor(currency, locationId));

  return { subtotal, discountTotal, cgstTotal, sgstTotal, igstTotal, grandTotal, lines };
}

/**
 * T19: the offline bill as a receipt reads it — the OFL number, what the
 * cashier saw on the cart, and per-line figures. Never sent to the server.
 */
function buildOfflineReceiptBill(args: {
  clientId: string;
  billNumber: string;
  createdAt: string;
  locationId: string | null;
  currency: CurrencyCode;
  items: CartLineItem[];
  lines: ComputedLine[];
  totals: OfflineReceiptTotals;
  paymentMethod: string;
  amountPaid: number;
  payments?: Array<{ payment_method: string; amount: number }>;
  customerId: string | null;
  customerName: string | null;
  customerPhone: string | null;
  /**
   * India GST: the split the till computed, so the popup shows it (gst-parked
   * 080); NONE when the business is not GST registered (090).
   */
  supplyType?: 'INTRA' | 'INTER' | 'NONE';
}): BillWithItems {
  const { clientId, createdAt } = args;
  const paymentRows = (args.payments?.length
    ? args.payments
    : [{ payment_method: args.paymentMethod, amount: args.amountPaid }]
  ).map((p, i) => ({
    id: `${clientId}-p${i}`,
    bill_id: clientId,
    tenant_id: '',
    payment_method: p.payment_method as BillPaymentMethod,
    amount: p.amount,
    created_at: createdAt,
  }));
  return {
    id: clientId,
    tenant_id: '',
    location_id: args.locationId ?? '',
    bill_number: args.billNumber,
    currency: args.currency,
    status: 'COMPLETED',
    subtotal: args.totals.subtotal,
    tax_total: args.totals.taxTotal,
    discount_total: args.totals.discountTotal,
    grand_total: args.totals.grandTotal,
    rounding_adjustment: args.totals.roundingAdjustment,
    payment_method: args.paymentMethod as BillWithItems['payment_method'],
    amount_paid: args.amountPaid,
    change_due: 0,
    customer_id: args.customerId,
    customer_name: args.customerName ?? undefined,
    customer_phone: args.customerPhone ?? undefined,
    ...(args.supplyType ? { supply_type: args.supplyType } : {}),
    created_at: createdAt,
    updated_at: createdAt,
    items: args.items.map((item, i) => {
      const line = args.lines[i];
      return {
        id: `${clientId}-${i}`,
        bill_id: clientId,
        tenant_id: '',
        product_id: item.productId,
        product_name: item.name,
        product_sku: item.sku,
        unit: item.unit,
        quantity: item.quantity,
        unit_price: item.unitPrice,
        unit_cost: item.unitCost,
        // Not GST registered (gst-parked 090): the line was charged at rate 0.
        gst_rate: gstWaived() ? 0 : item.gstRate,
        hsn_code: item.hsnCode,
        line_subtotal: line.lineSubtotal,
        line_tax: line.lineTax,
        line_discount: line.lineDiscount,
        line_total: line.lineSubtotal - line.lineDiscount + line.lineTax,
      };
    }),
    payments: paymentRows,
  };
}

// ─── Service ────────────────────────────────────────────────────────────────

export const offlineBillingService = {
  /**
   * Create and queue a bill for offline sync.
   * Uses syncQueueService.enqueue() so listeners are notified.
   * Returns the client_id, the OFL number and the bill as its receipt prints it (T19).
   */
  async createBillOffline(params: {
    items: CartLineItem[];
    customerId: string | null;
    locationId: string | null;
    locationCode: string;
    paymentMethod: string;
    amountPaid: number;
    roundingAdjustment?: number;
    isInterState: boolean;
    /** gst-parked D7: the cashier's IGST switch; null / absent = the store's own split. */
    supplyOverride?: 'INTRA' | 'INTER' | null;
    billDiscount?: BillDiscount | null;
    payments?: Array<{ payment_method: string; amount: number }>;
    /** T19: slip / UPI reference the cashier confirmed for a card/UPI payment. */
    paymentReference?: string | null;
    /** T19: who it was for, shown and searched in the offline-bills list. */
    customerName?: string | null;
    customerPhone?: string | null;
    /** T19: the cart totals the receipt prints; derived from the lines when absent. */
    receiptTotals?: OfflineReceiptTotals;
  }): Promise<OfflineBillResult> {
    const clientId = crypto.randomUUID();
    const now = new Date().toISOString();
    const billNumber = generateOfflineBillNumber(params.locationCode);

    // Mirror the online checkout's applyDiscount units: PERCENTAGE → basis
    // points (Math.round(value * 100)), FLAT → paise as-is. `amountPaid` was
    // already computed from the discounted grand total, so this lets the
    // server rebuild the same total on sync instead of rejecting the bill.
    const billDiscountPayload = params.billDiscount
      ? {
          type: params.billDiscount.type,
          value:
            params.billDiscount.type === 'PERCENTAGE'
              ? Math.round(params.billDiscount.value * 100)
              : params.billDiscount.value,
          reason: params.billDiscount.reason,
        }
      : undefined;

    // The tenant's immutable currency, cached offline (task 280). Drives
    // subunit-aware + regime-aware client-side tax/rounding only. It is
    // intentionally NOT added to the payload: currency is immutable, so the
    // server safely re-stamps it from the tenant default at sync time —
    // server-side currency stamping is unchanged.
    const currency = await getCachedTenantCurrency();
    const totals = computeItemTotals(params.items, params.isInterState, currency, params.locationId);

    const registerSessionId = getActiveRegisterSessionId(params.locationId);
    const payload: OfflineBillPayload = {
      client_id: clientId,
      location_id: params.locationId,
      ...(registerSessionId ? { register_session_id: registerSessionId } : {}),
      items: params.items.map((item) => ({
        product_id: item.productId,
        name: item.name,
        sku: item.sku,
        unit: item.unit,
        quantity: item.quantity,
        unit_price: item.unitPrice,
        unit_cost: item.unitCost,
        // The rate the till charged: 0 when not GST registered (gst-parked 090),
        // so the server's T09 check and an owner's accept-as-sold see what was sold.
        gst_rate: gstWaived() ? 0 : item.gstRate,
        hsn_code: item.hsnCode,
        ...(item.discountType && item.discountValue > 0
          ? { discount_type: item.discountType, discount_value: item.discountValue }
          : {}),
      })),
      customer_id: params.customerId,
      bill_number: billNumber,
      payment_method: params.paymentMethod,
      amount_paid: params.amountPaid,
      ...(params.roundingAdjustment ? { rounding_adjustment: params.roundingAdjustment } : {}),
      ...(billDiscountPayload ? { bill_discount: billDiscountPayload } : {}),
      subtotal: totals.subtotal,
      discount_total: totals.discountTotal,
      cgst_total: totals.cgstTotal,
      sgst_total: totals.sgstTotal,
      igst_total: totals.igstTotal,
      grand_total: totals.grandTotal,
      is_inter_state: params.isInterState,
      ...(params.supplyOverride ? { supply_override: params.supplyOverride } : {}),
      payments: params.payments,
      created_at: now,
      ...(params.paymentReference?.trim() ? { payment_reference: params.paymentReference.trim() } : {}),
    };

    const customerLabel =
      [params.customerName, params.customerPhone].filter((v) => !!v && String(v).trim()).join(' · ') || null;

    await enqueue({
      client_id: clientId,
      entity_type: 'BILL',
      operation: 'CREATE',
      payload: payload as unknown as Record<string, unknown>,
      device_id: deviceId,
      status: 'PENDING',
      created_at: now,
      retry_count: 0,
      last_error: null,
      customer_label: customerLabel,
    });

    const receiptTotals: OfflineReceiptTotals = params.receiptTotals ?? {
      subtotal: totals.subtotal,
      discountTotal: totals.discountTotal,
      taxTotal: totals.cgstTotal + totals.sgstTotal + totals.igstTotal,
      grandTotal: totals.grandTotal,
      roundingAdjustment: params.roundingAdjustment ?? 0,
    };

    return {
      clientId,
      billNumber,
      receiptBill: buildOfflineReceiptBill({
        clientId,
        billNumber,
        createdAt: now,
        locationId: params.locationId,
        currency,
        items: params.items,
        lines: totals.lines,
        totals: receiptTotals,
        paymentMethod: params.paymentMethod,
        amountPaid: params.amountPaid,
        payments: params.payments,
        customerId: params.customerId,
        customerName: params.customerName ?? null,
        customerPhone: params.customerPhone ?? null,
        ...(getTaxRegime(currency) === 'GST_INDIA'
          ? {
              supplyType: gstWaived()
                ? ('NONE' as const)
                : params.isInterState ? ('INTER' as const) : ('INTRA' as const),
            }
          : {}),
      }),
    };
  },

  /**
   * Save current cart as a draft bill in IndexedDB for crash recovery.
   * Preserves `created_at` on updates by reading the existing draft first.
   */
  async saveDraft(params: {
    clientId: string;
    items: CartLineItem[];
    customerId: string | null;
    customerName: string | null;
    customerPhone: string | null;
    locationId: string | null;
    billMode: string;
    isInterState: boolean;
    /** gst-parked D7: the cashier's IGST switch; absent / null = the store's own. */
    supplyOverride?: 'INTRA' | 'INTER' | null;
    isParked: boolean;
    parkedReference: string | null;
    billDiscount?: DraftBillDiscount | null;
    pendingDiscountApproval?: DraftPendingDiscountApproval | null;
    serverBillId?: string | null;
    /** gst-parked 120: who parked it — kept with an offline park. */
    userId?: string | null;
    /** GP2-001: the parked row's total, as the cart showed it. */
    parkedTotal?: number;
  }): Promise<void> {
    const now = new Date().toISOString();

    // Preserve original created_at if draft already exists
    const existing = await decryptFromStorage(await posDb.draft_bills.get(params.clientId));
    const createdAt = existing?.created_at ?? now;

    const draftItems: DraftBillItem[] = params.items.map((item) => ({
      id: item.id,
      productId: item.productId,
      name: item.name,
      sku: item.sku,
      unit: item.unit,
      quantity: item.quantity,
      unitPrice: item.unitPrice,
      unitCost: item.unitCost,
      gstRate: item.gstRate,
      hsnCode: item.hsnCode,
      ...(item.listPrice !== undefined ? { listPrice: item.listPrice } : {}),
      discountType: item.discountType,
      discountValue: item.discountValue,
    }));

    const draft: DraftBill = {
      client_id: params.clientId,
      location_id: params.locationId,
      items: draftItems,
      customer_id: params.customerId,
      customer_name: params.customerName,
      customer_phone: params.customerPhone,
      bill_mode: params.billMode,
      is_inter_state: params.isInterState,
      supply_override: params.supplyOverride ?? null,
      is_parked: params.isParked ? 1 : 0,
      parked_reference: params.parkedReference,
      bill_discount: params.billDiscount ?? null,
      pending_discount_approval: params.pendingDiscountApproval ?? null,
      server_bill_id: params.serverBillId ?? null,
      ...(params.userId ? { user_id: params.userId } : {}),
      ...(params.parkedTotal !== undefined ? { parked_total: params.parkedTotal } : {}),
      created_at: createdAt,
      updated_at: now,
    };

    const encrypted = await encryptForStorage(draft, 'draft_bills');
    await posDb.draft_bills.put(encrypted);
  },

  /**
   * Load the active (non-parked) draft bill, if any.
   */
  async loadActiveDraft(): Promise<DraftBill | undefined> {
    return decryptFromStorage(
      await posDb.draft_bills.where('is_parked').equals(0).first(),
    );
  },

  /**
   * Load the parked draft bills — with `locationId`, only that store's
   * (gst-parked 120: a till lists the parked bills of the store it bills at).
   */
  async loadParkedDrafts(locationId?: string | null): Promise<DraftBill[]> {
    const rows = await decryptManyFromStorage(
      await posDb.draft_bills.where('is_parked').equals(1).toArray(),
    );
    return locationId === undefined ? rows : rows.filter((r) => r.location_id === locationId);
  },

  /**
   * gst-parked 120: record an offline park's upload progress — the server
   * draft it created, or why the server refused it. Re-encrypts the row.
   */
  async patchParkedDraft(
    clientId: string,
    patch: Pick<Partial<DraftBill>, 'server_bill_id' | 'upload_error'>,
  ): Promise<void> {
    const existing = await decryptFromStorage(await posDb.draft_bills.get(clientId));
    if (!existing || !Array.isArray(existing.items)) return;
    const next: DraftBill = { ...existing, ...patch, updated_at: new Date().toISOString() };
    await posDb.draft_bills.put(await encryptForStorage(next, 'draft_bills'));
  },

  /**
   * Load a specific draft bill by client_id.
   */
  async loadDraft(clientId: string): Promise<DraftBill | undefined> {
    return decryptFromStorage(await posDb.draft_bills.get(clientId));
  },

  /**
   * Remove a draft bill from IndexedDB.
   */
  async removeDraft(clientId: string): Promise<void> {
    await posDb.draft_bills.delete(clientId);
  },

  /**
   * Clear all non-parked drafts (e.g. after successful checkout).
   */
  async clearActiveDrafts(): Promise<void> {
    await posDb.draft_bills.where('is_parked').equals(0).delete();
  },

  /**
   * Clear all drafts — active and parked (e.g. on logout).
   */
  async clearAllDrafts(): Promise<void> {
    await posDb.draft_bills.clear();
  },
};
